import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Close as CloseIcon, FormatListBulleted, AccountTree } from '@mui/icons-material';
import PropertyList from '@/components/PropertyList';
import PropertyForm from '@/components/PropertyForm';
import { Property, PropertyFormData, PropertySearchFilters, PropertyType, ListingType } from '@/types';
import { PropertyService } from '@/services/propertyService';

const PropertiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(0);

  // Parse filters from URL
  const filters = useMemo(() => {
    const f: PropertySearchFilters = {};
    
    // Parse arrays (comma separated)
    const types = searchParams.get('types');
    if (types) f.propertyTypes = types.split(',') as PropertyType[];
    
    const listingTypes = searchParams.get('listingTypes');
    if (listingTypes) f.listingTypes = listingTypes.split(',') as ListingType[];
    
    const features = searchParams.get('features');
    if (features) f.features = features.split(',');

    const amenities = searchParams.get('amenities');
    if (amenities) f.amenities = amenities.split(',');

    // Parse ranges
    const minPrice = searchParams.get('minPrice');
    const maxPrice = searchParams.get('maxPrice');
    if (minPrice || maxPrice) {
      f.priceRange = {
        min: minPrice ? parseInt(minPrice) : 0,
        max: maxPrice ? parseInt(maxPrice) : 10000000
      };
    }

    const minBeds = searchParams.get('minBeds');
    const maxBeds = searchParams.get('maxBeds');
    if (minBeds || maxBeds) {
      f.bedrooms = {
        min: minBeds ? parseInt(minBeds) : 0,
        max: maxBeds ? parseInt(maxBeds) : 10
      };
    }

    const minBaths = searchParams.get('minBaths');
    const maxBaths = searchParams.get('maxBaths');
    if (minBaths || maxBaths) {
      f.bathrooms = {
        min: minBaths ? parseInt(minBaths) : 0,
        max: maxBaths ? parseInt(maxBaths) : 10
      };
    }

    const minSqft = searchParams.get('minSqft');
    const maxSqft = searchParams.get('maxSqft');
    if (minSqft || maxSqft) {
      f.squareFootage = {
        min: minSqft ? parseInt(minSqft) : 0,
        max: maxSqft ? parseInt(maxSqft) : 10000
      };
    }

    // Parse location
    const city = searchParams.get('city');
    const state = searchParams.get('state');
    const zip = searchParams.get('zip');
    if (city || state || zip) {
      f.location = {
        city: city || undefined,
        state: state || undefined,
        zipCode: zip || undefined
      };
    }

    // Parse booleans
    const active = searchParams.get('active');
    if (active) f.isActive = active === 'true';
    
    const featured = searchParams.get('featured');
    if (featured) f.isFeatured = featured === 'true';

    const search = searchParams.get('search');
    if (search) f.searchTerm = search;

    return f;
  }, [searchParams]);

  const handleFilterChange = (newFilters: PropertySearchFilters) => {
    const params = new URLSearchParams();

    if (newFilters.searchTerm) {
      params.set('search', newFilters.searchTerm);
    }

    if (newFilters.propertyTypes?.length) {
      params.set('types', newFilters.propertyTypes.join(','));
    }
    if (newFilters.listingTypes?.length) {
      params.set('listingTypes', newFilters.listingTypes.join(','));
    }
    if (newFilters.features?.length) {
      params.set('features', newFilters.features.join(','));
    }
    if (newFilters.amenities?.length) {
      params.set('amenities', newFilters.amenities.join(','));
    }

    if (newFilters.priceRange) {
      if (newFilters.priceRange.min > 0) params.set('minPrice', newFilters.priceRange.min.toString());
      if (newFilters.priceRange.max < 10000000) params.set('maxPrice', newFilters.priceRange.max.toString());
    }

    if (newFilters.bedrooms) {
      if (newFilters.bedrooms.min > 0) params.set('minBeds', newFilters.bedrooms.min.toString());
      if (newFilters.bedrooms.max < 10) params.set('maxBeds', newFilters.bedrooms.max.toString());
    }

    if (newFilters.bathrooms) {
      if (newFilters.bathrooms.min > 0) params.set('minBaths', newFilters.bathrooms.min.toString());
      if (newFilters.bathrooms.max < 10) params.set('maxBaths', newFilters.bathrooms.max.toString());
    }

    if (newFilters.squareFootage) {
      if (newFilters.squareFootage.min > 0) params.set('minSqft', newFilters.squareFootage.min.toString());
      if (newFilters.squareFootage.max < 10000) params.set('maxSqft', newFilters.squareFootage.max.toString());
    }

    if (newFilters.location) {
      if (newFilters.location.city) params.set('city', newFilters.location.city);
      if (newFilters.location.state) params.set('state', newFilters.location.state);
      if (newFilters.location.zipCode) params.set('zip', newFilters.location.zipCode);
    }

    if (newFilters.isActive !== undefined) {
      params.set('active', newFilters.isActive.toString());
    }
    if (newFilters.isFeatured !== undefined) {
      params.set('featured', newFilters.isFeatured.toString());
    }

    setSearchParams(params);
  };

  const handleCreatePropertyClick = () => {
    navigate('/properties/create');
  };

  const handleEditProperty = async (data: PropertyFormData, files?: File[], documentFiles?: File[]) => {
    if (!selectedProperty) return;

    setLoading(true);
    try {
      // 1. Upload new images if any
      let newImageUrls: string[] = [];
      if (files && files.length > 0) {
         await Promise.all(
          files.map(async (file, index) => {
            try {
              const url = await PropertyService.uploadPropertyImage(selectedProperty.id, file, index);
              newImageUrls.push(url);
            } catch (err) {
              console.error('Failed to upload image:', err);
            }
          })
        );
      }

      // 2. Upload new documents if any
      let newDocumentUrls: string[] = [];
      if (documentFiles && documentFiles.length > 0) {
         await Promise.all(
          documentFiles.map(async (file) => {
            try {
              // Extract file extension or name as type
              const docType = 'doc'; 
              const url = await PropertyService.uploadPropertyDocument(selectedProperty.id, file, docType);
              newDocumentUrls.push(url);
            } catch (err) {
              console.error('Failed to upload document:', err);
            }
          })
        );
      }

      // 3. Merge new images/docs with existing
      const updatedImages = [...(data.metadata.images || []), ...newImageUrls];
      const updatedDocuments = [...(data.metadata.documents || []), ...newDocumentUrls];
      
      const updatedData = {
        ...data,
        metadata: {
          ...data.metadata,
          images: updatedImages,
          documents: updatedDocuments
        }
      };

      await PropertyService.updateProperty(selectedProperty.id, updatedData);
      setEditDialogOpen(false);
      setSelectedProperty(null);
      window.location.reload(); // Simple refresh for now
    } catch (error: any) {
      throw error; // Will be caught by PropertyForm
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProperty = async (property: Property) => {
    if (!window.confirm(`Are you sure you want to delete "${property.metadata.title}"?`)) {
      return;
    }

    try {
      await PropertyService.deleteProperty(property.id, false); // Soft delete
      window.location.reload(); // Simple refresh for now
    } catch (error: any) {
      alert(`Failed to delete property: ${error.message}`);
    }
  };

  const handlePropertyClick = (property: Property) => {
    navigate(`/properties/${property.id}`);
  };

  const handlePropertyEdit = (property: Property) => {
    setSelectedProperty(property);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedProperty(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 600, color: 'text.primary', fontFamily: '"Playfair Display", serif' }}>
            Unit Inventory
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Centralized management across all Chantiers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tabs value={viewMode} onChange={(_, val) => setViewMode(val)} sx={{ mr: 2 }}>
            <Tab icon={<FormatListBulleted />} label="All Units" iconPosition="start" />
            <Tab icon={<AccountTree />} label="By Chantier" iconPosition="start" />
          </Tabs>
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={handleCreatePropertyClick}
            sx={{ boxShadow: 'none' }}
          >
            New Unit
          </Button>
        </Box>
      </Box>

      {viewMode === 0 ? (
        <PropertyList
          filters={filters}
          onFilterChange={handleFilterChange}
          onPropertyClick={handlePropertyClick}
          onPropertyEdit={handlePropertyEdit}
          onPropertyDelete={handleDeleteProperty}
          showFilters={true}
        />
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(197, 160, 89, 0.05)', borderRadius: 2, border: '1px dashed rgba(197, 160, 89, 0.3)' }}>
          <AccountTree sx={{ fontSize: 48, color: 'primary.main', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif' }}>Chantier Master View Active</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Grouping units by Marina Blanca, Anfa Sky Towers, and Le Jardin d'Océan. This prevents duplicate or ghost listings.
          </Typography>
        </Box>
      )}

      {/* Edit Property Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Property
          <IconButton
            aria-label="close"
            onClick={handleCloseEditDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedProperty && (
            <PropertyForm
              property={selectedProperty}
              onSubmit={handleEditProperty}
              onCancel={handleCloseEditDialog}
              loading={loading}
            />
          )}
        </DialogContent>
      </Dialog>

      <Fab
        color="primary"
        aria-label="add property"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleCreatePropertyClick}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default PropertiesPage;
